import { compact } from 'lodash';
import {
  IconArrowTrendUpOutlineDuo18,
  IconBookmarkOutlineDuo18,
  IconBookOpen5OutlineDuo18,
  IconBooks2OutlineDuo18,
  IconBooksOutlineDuo18,
  IconCalendarClockOutlineDuo18,
  IconChart3OutlineDuo18,
  IconCircleQuestionOutlineDuo18,
  IconClipboardContentOutlineDuo18,
  IconClipboardDocumentOutlineDuo18,
  IconClipboardListOutlineDuo18,
  IconCompassOutlineDuo18,
  IconCrosshairsOutlineDuo18,
  IconCursorRippleOutlineDuo18,
  IconFolderOutlineDuo18,
  IconGauge7OutlineDuo18,
  IconGlobeOutlineDuo18,
  IconGridOutlineDuo18,
  IconGridSquareCirclePlusOutlineDuo18,
  IconHeartHandshakeOutlineDuo18,
  IconHouseOutlineDuo18,
  IconInboxOutlineDuo18,
  IconLayersOutlineDuo18,
  IconMapPinOutlineDuo18,
  IconMessageContentOutlineDuo18,
  IconMessagesOutlineDuo18,
  IconMsgSparkleOutlineDuo18,
  IconNut2OutlineDuo18,
  IconRepeatOutlineDuo18,
  IconRocketOutlineDuo18,
  IconSitemapOutlineDuo18,
  IconSlidersOutlineDuo18,
  IconSquareLayoutGrid4OutlineDuo18,
  IconStarOutlineDuo18,
  IconSuitcaseOutlineDuo18,
  IconTargetOutlineDuo18,
  IconTasksOutlineDuo18,
  IconTv2OutlineDuo18,
  IconUpload3OutlineDuo18,
  IconUsersHeartOutlineDuo18,
  IconUsersOutlineDuo18,
  IconVideoOutlineDuo18,
} from 'nucleo-ui-outline-duo-18';

import { TFunction } from '@lyearn/i18n';
import { FeatureFlags, FeatureFlagsMap } from '@/helper/FeatureFlags';
import { FunctionalPermissionKey } from '@/helper/FunctionalPermissions';
import { DEFAULT_FEEDBACK_TAB } from '@/modules/corporate/modules/feedback/views/FeedbackWall/consts';
import { PraisesTabEnum } from '@/modules/praises/views/PraiseWall/types';
import { QuestionManagerTabEnum } from '@/modules/question/views/QuestionManager/types';
import { MyUserSurveysTabType } from '@/modules/user-survey/views/MyUserSurveys/types';
import routes from '@/pages/corporate/routes';
import workeffyAdminRoutes from '@/pages/corporate/routes/workeffy';

import { NAV_GROUP_IDS, NAV_IDS, NAV_SECTION_IDS } from '../consts';
import { NavItem, NavSection, NavSubGroup, NavSubItem, UnifiedNav } from '../types';

export interface BuildUnifiedNavOptions {
  t: TFunction;
  flags: FeatureFlagsMap;
  communityId?: string;
  defaultWidgetId?: string;
  isQuestionBankDisabled?: boolean;
  hasPermission: (permission: FunctionalPermissionKey) => boolean;
}

const toGroup = (id: string, items: (NavSubItem | null)[]): NavSubGroup | null => {
  const enabledItems = compact(items);
  const hasRealContent = enabledItems.some((item) => !item.isDisabled);

  return hasRealContent ? { id, items: enabledItems } : null;
};

const toParent = (item: NavItem, groups: (NavSubGroup | null)[]): NavItem | null => {
  const enabledGroups = compact(groups);

  return enabledGroups.length ? { ...item, groups: enabledGroups } : null;
};

const toSection = (id: string, items: (NavItem | null)[]): NavSection | null => {
  const enabledItems = compact(items);

  return enabledItems.length ? { id, items: enabledItems } : null;
};

// Mirrors effy-fe's COACH_AREA_PERMISSIONS: chat, plus every agent-builder permission.
const COACH_AREA_PERMISSIONS = [
  FunctionalPermissionKey.UseAgentChat,
  FunctionalPermissionKey.UseSystemAgent,
  FunctionalPermissionKey.UseCustomAgent,
  FunctionalPermissionKey.CreateCustomAgent,
  FunctionalPermissionKey.CreateCustomWorkflow,
  FunctionalPermissionKey.ManageRoutine,
];

// @DEV_CHECKPOINT - sidebar
export const buildUnifiedNav = ({
  t,
  flags,
  communityId,
  defaultWidgetId,
  isQuestionBankDisabled,
  hasPermission,
}: BuildUnifiedNavOptions): UnifiedNav => {
  const {
    [FeatureFlags.EnableOnboarding]: isOnboardingEnabled,
    [FeatureFlags.EnabledMGSAOnboarding]: isMgsaOnboardingEnabled,
    [FeatureFlags.EnableProductivityModel]: isProductivityModelEnabled,
    [FeatureFlags.EnablePerformModule]: isPerformModuleEnabled,
    [FeatureFlags.EnableLearnModule]: isLearnModuleEnabled,
    [FeatureFlags.EnableEngageModule]: isEngageModuleEnabled,
    [FeatureFlags.EnableOKR]: isOkrEnabled,
    [FeatureFlags.EnableHabits]: isHabitsEnabled,
    [FeatureFlags.EnableEmployeeJourney]: isEmployeeJourneyEnabled,
    [FeatureFlags.EnableUserSurvey]: isUserSurveyEnabled,
    [FeatureFlags.EnableMeeting]: isMeetingEnabled,
    [FeatureFlags.EnableOneOnOne]: isOneOnOneEnabled,
    [FeatureFlags.EnablePraise]: isPraiseEnabled,
    [FeatureFlags.EnableFeedback]: isFeedbackEnabled,
  } = flags;

  const primary = toSection(NAV_SECTION_IDS.Primary, [
    (isOnboardingEnabled || isMgsaOnboardingEnabled) &&
    hasPermission(FunctionalPermissionKey.ManageAcademy)
      ? {
          id: NAV_IDS.GetStarted,
          label: t('Admin:GettingStarted'),
          icon: IconRocketOutlineDuo18,
          href: routes.GettingStarted(),
        }
      : null,
    {
      id: NAV_IDS.Home,
      label: t('Common:Routes.Home'),
      icon: IconHouseOutlineDuo18,
      href: routes.Home(),
    },
    hasPermission(FunctionalPermissionKey.UseAgentDraft) ||
    hasPermission(FunctionalPermissionKey.ManageOrgChart)
      ? {
          id: NAV_IDS.Actions,
          label: t('Common:Routes.Actions', 'Actions'),
          icon: IconCursorRippleOutlineDuo18,
          href: routes.WorkeffyActions(),
          matchPrefix: true,
        }
      : null,
    COACH_AREA_PERMISSIONS.some(hasPermission)
      ? {
          id: NAV_IDS.Agents,
          label: t('Common:Routes.Coach', 'Coach'),
          icon: IconMsgSparkleOutlineDuo18,
          href: routes.WorkeffyAgents(),
          matchPrefix: true,
        }
      : null,
  ]);

  const people = toParent(
    {
      id: NAV_IDS.People,
      label: t('Common:Routes.People', 'People'),
      icon: IconUsersHeartOutlineDuo18,
    },
    [
      toGroup(NAV_GROUP_IDS.PeopleMain, [
        {
          id: NAV_IDS.PeoplePulse,
          label: t('Common:Routes.PeoplePulse', 'People Pulse'),
          isDisabled: true,
        },
        hasPermission(FunctionalPermissionKey.ViewCareerFramework)
          ? {
              id: NAV_IDS.CareerPaths,
              label: t('Common:Routes.CareerPaths', 'Career Paths'),
              icon: IconArrowTrendUpOutlineDuo18,
              href: workeffyAdminRoutes.WorkeffyCareerPaths(),
              matchPrefix: true,
            }
          : null,
        hasPermission(FunctionalPermissionKey.ViewOrgChart)
          ? {
              id: NAV_IDS.OrgStructure,
              label: t('Common:Routes.OrgStructure', 'Org Structure'),
              icon: IconSitemapOutlineDuo18,
              href: routes.WorkeffyOrgStructure(),
              matchPrefix: true,
            }
          : null,
        hasPermission(FunctionalPermissionKey.ViewOrgChart)
          ? {
              id: NAV_IDS.JobArchitecture,
              label: t('Common:Routes.JobArchitecture', 'Job Architecture'),
              icon: IconSuitcaseOutlineDuo18,
              href: workeffyAdminRoutes.WorkeffyJobArchitecture(),
            }
          : null,
        hasPermission(FunctionalPermissionKey.ViewOrgChart)
          ? {
              id: NAV_IDS.Locations,
              label: t('Common:Routes.Locations', 'Locations'),
              icon: IconMapPinOutlineDuo18,
              href: workeffyAdminRoutes.WorkeffyLocations(),
              matchPrefix: true,
            }
          : null,
        hasPermission(FunctionalPermissionKey.ManageUsers)
          ? {
              id: NAV_IDS.Employees,
              label: t('Common:Routes.PeopleDirectory', 'People Directory'),
              icon: IconUsersOutlineDuo18,
              href: workeffyAdminRoutes.WorkeffyPeopleDirectory(),
              matchPrefix: true,
            }
          : null,
      ]),
    ],
  );

  const productivity = toParent(
    {
      id: NAV_IDS.Productivity,
      label: t('Common:Routes.Productivity', 'Productivity'),
      icon: IconGauge7OutlineDuo18,
    },
    [
      toGroup(NAV_GROUP_IDS.ProductivityOverview, [
        {
          id: NAV_IDS.ProductivityPulse,
          label: t('Common:Routes.ProductivityPulse', 'Productivity Pulse'),
          isDisabled: true,
        },
        isProductivityModelEnabled && hasPermission(FunctionalPermissionKey.ManageProductivityModel)
          ? {
              id: NAV_IDS.Mgsa,
              label: t('Common:Routes.MGSA', 'Configure MGSA'),
              icon: IconTargetOutlineDuo18,
              href: workeffyAdminRoutes.WorkeffyMgsa(),
              matchPrefix: true,
            }
          : null,
      ]),
      toGroup(NAV_GROUP_IDS.ProductivityOkrs, [
        hasPermission(FunctionalPermissionKey.UseOkr)
          ? {
              id: NAV_IDS.Okrs,
              label: t('Common:Routes.OKRs'),
              icon: IconCrosshairsOutlineDuo18,
              href: routes.WorkEffyAllOKRs(),
            }
          : null,
        hasPermission(FunctionalPermissionKey.UseOkr)
          ? {
              id: NAV_IDS.Cycles,
              label: t('Common:Routes.Cycles'),
              icon: IconCalendarClockOutlineDuo18,
              href: routes.WorkEffyManageCycles(),
            }
          : null,
      ]),
      toGroup(NAV_GROUP_IDS.ProductivityPerform, [
        hasPermission(FunctionalPermissionKey.ViewTask)
          ? {
              id: NAV_IDS.Tasks,
              label: t('Common:Routes.Tasks'),
              icon: IconTasksOutlineDuo18,
              href: routes.Tasks(),
              matchPrefix: true,
              activeRoutes: [routes.Projects()],
            }
          : null,
        hasPermission(FunctionalPermissionKey.ViewHabit)
          ? {
              id: NAV_IDS.Habits,
              label: t('Common:Routes.Habits'),
              icon: IconRepeatOutlineDuo18,
              href: routes.Habits(),
              matchPrefix: true,
            }
          : null,
      ]),
    ],
  );

  const learning = isLearnModuleEnabled
    ? toParent(
        {
          id: NAV_IDS.Learning,
          label: t('Common:Routes.Learning', 'Learning'),
          icon: IconBookOpen5OutlineDuo18,
        },
        [
          toGroup(NAV_GROUP_IDS.LearningOverview, [
            {
              id: NAV_IDS.LearningPulse,
              label: t('Common:Routes.LearningPulse', 'Learning Pulse'),
              isDisabled: true,
            },
            hasPermission(FunctionalPermissionKey.ViewContent)
              ? {
                  id: NAV_IDS.Library,
                  label: t('Common:Routes.Library'),
                  icon: IconBooksOutlineDuo18,
                  href: defaultWidgetId ? routes.Discover(defaultWidgetId) : undefined,
                  activeRoutes: [routes.DiscoverRoot()],
                  isDisabled: !defaultWidgetId,
                }
              : null,
            hasPermission(FunctionalPermissionKey.ViewBookmarks)
              ? {
                  id: NAV_IDS.Bookmark,
                  label: t('Common:Routes.Bookmark', 'Bookmark'),
                  icon: IconBookmarkOutlineDuo18,
                  href: routes.Bookmarks(),
                }
              : null,
          ]),
          toGroup(NAV_GROUP_IDS.LearningContent, [
            hasPermission(FunctionalPermissionKey.ViewAllContent)
              ? {
                  id: NAV_IDS.AllContent,
                  label: t('Admin:AllContent'),
                  icon: IconGridSquareCirclePlusOutlineDuo18,
                  href: routes.LearningContent(),
                  activeRoutes: [
                    routes.LearningContentDraft(),
                    routes.LearningContentArchived(),
                    routes.RecentLearningContent(),
                  ],
                }
              : null,
            hasPermission(FunctionalPermissionKey.ViewAllCollection)
              ? {
                  id: NAV_IDS.Collections,
                  label: t('Admin:Collections'),
                  icon: IconLayersOutlineDuo18,
                  href: routes.CollectionManager(),
                  matchPrefix: true,
                }
              : null,
            isQuestionBankDisabled || !hasPermission(FunctionalPermissionKey.AuthorContent)
              ? null
              : {
                  id: NAV_IDS.QuestionBank,
                  label: t('Admin:QuestionBank'),
                  icon: IconCircleQuestionOutlineDuo18,
                  href: routes.AdminQuestionManager(QuestionManagerTabEnum.Active),
                  activeRoutes: [routes.AdminQuestionBank()],
                },
          ]),
          toGroup(NAV_GROUP_IDS.LearningDelivery, [
            // Opens the learner sessions page; the admin manager is reached from
            // there via Manage Sessions, which is gated on MANAGE_SESSION.
            hasPermission(FunctionalPermissionKey.ViewSession)
              ? {
                  id: NAV_IDS.Sessions,
                  label: t('Common:Routes.Sessions'),
                  icon: IconTv2OutlineDuo18,
                  href: routes.DiscoverAllEvent(),
                  activeRoutes: [
                    routes.DiscoverEvents(),
                    routes.ManageUpcomingEvents(),
                    routes.ManageFinishedEvents(),
                    routes.ManageRecurringEvents(),
                    routes.AdminEventRoot(),
                  ],
                }
              : null,
            hasPermission(FunctionalPermissionKey.ViewAllAssignment)
              ? {
                  id: NAV_IDS.Assignment,
                  label: t('Common:Routes.Assignment', 'Assignment'),
                  icon: IconClipboardDocumentOutlineDuo18,
                  href: routes.Assignments(),
                  matchPrefix: true,
                  activeRoutes: [routes.AssignmentRoot()],
                }
              : null,
            isEmployeeJourneyEnabled && hasPermission(FunctionalPermissionKey.ViewJourney)
              ? {
                  id: NAV_IDS.Journeys,
                  label: t('Common:Routes.Journeys'),
                  icon: IconCompassOutlineDuo18,
                  href: routes.JourneyListing(),
                  matchPrefix: true,
                  // Manage Journeys opens the admin manager from the listing.
                  activeRoutes: [routes.JourneyManager()],
                }
              : null,
          ]),
        ],
      )
    : null;

  const engagement = isEngageModuleEnabled
    ? toParent(
        {
          id: NAV_IDS.Engagement,
          label: t('Common:Routes.Engagement', 'Engagement'),
          icon: IconHeartHandshakeOutlineDuo18,
        },
        [
          toGroup(NAV_GROUP_IDS.EngagementMain, [
            isUserSurveyEnabled && hasPermission(FunctionalPermissionKey.ViewSurvey)
              ? {
                  id: NAV_IDS.Surveys,
                  label: t('Common:Routes.Surveys'),
                  icon: IconClipboardListOutlineDuo18,
                  href: routes.MySurveys(MyUserSurveysTabType.Surveys),
                  activeRoutes: [
                    routes.MySurveys(),
                    routes.MySurveyRoot(),
                    routes.UserSurveyLearnerRoot(),
                    routes.LearnerSurveyManager(),
                    routes.UserSurveys(),
                    routes.UserSurveyAdminRoot(),
                  ],
                }
              : null,
            communityId && hasPermission(FunctionalPermissionKey.ViewCommunity)
              ? {
                  id: NAV_IDS.Community,
                  label: t('Common:Routes.Community'),
                  icon: IconGlobeOutlineDuo18,
                  href: routes.CommunityDetails(communityId),
                  matchPrefix: true,
                }
              : null,
            isMeetingEnabled && hasPermission(FunctionalPermissionKey.ViewMeeting)
              ? {
                  id: NAV_IDS.Meetings,
                  label: t('Common:Routes.Meetings'),
                  icon: IconVideoOutlineDuo18,
                  href: routes.teamMeetingsDashboard(),
                  matchPrefix: true,
                }
              : null,
            isOneOnOneEnabled && hasPermission(FunctionalPermissionKey.ViewOneOnOne)
              ? {
                  id: NAV_IDS.OneOnOne,
                  label: t('Common:Routes.OneOnOne', '1:1'),
                  icon: IconMessagesOutlineDuo18,
                  href: routes.oneOnOnes(),
                  matchPrefix: true,
                  activeRoutes: [routes.SystemAgendaManager()],
                }
              : null,
            isPraiseEnabled && hasPermission(FunctionalPermissionKey.ViewPraise)
              ? {
                  id: NAV_IDS.Praise,
                  label: t('Common:Routes.Praise'),
                  icon: IconStarOutlineDuo18,
                  href: routes.Praises(PraisesTabEnum.All),
                  activeRoutes: [routes.Praises()],
                  matchPrefix: true,
                }
              : null,
            isFeedbackEnabled && hasPermission(FunctionalPermissionKey.ViewFeedback)
              ? {
                  id: NAV_IDS.Feedback,
                  label: t('Common:Routes.Feedback'),
                  icon: IconMessageContentOutlineDuo18,
                  href: routes.Feedback(DEFAULT_FEEDBACK_TAB),
                  activeRoutes: [routes.Feedback()],
                  matchPrefix: true,
                }
              : null,
          ]),
        ],
      )
    : null;

  // The Documents pages live in effy-fe (/w/documents); manage is for library managers.
  const documents = hasPermission(FunctionalPermissionKey.UseDocuments)
    ? toParent(
        {
          id: NAV_IDS.Documents,
          label: t('Common:Routes.Documents', 'Documents'),
          icon: IconFolderOutlineDuo18,
        },
        [
          toGroup(NAV_GROUP_IDS.DocumentsMain, [
            {
              id: NAV_IDS.DocumentsLibraries,
              label: t('Common:Routes.DocumentsLibraries', 'Libraries'),
              icon: IconBooks2OutlineDuo18,
              href: workeffyAdminRoutes.WorkeffyDocuments(),
              matchPrefix: true,
            },
            {
              id: NAV_IDS.DocumentsForYou,
              label: t('Common:Routes.DocumentsForYou', 'For you'),
              icon: IconInboxOutlineDuo18,
              href: workeffyAdminRoutes.WorkeffyDocumentsForYou(),
            },
            hasPermission(FunctionalPermissionKey.ManageDocumentLibraries)
              ? {
                  id: NAV_IDS.DocumentsManage,
                  label: t('Common:Routes.DocumentsManage', 'Manage Docs'),
                  icon: IconSlidersOutlineDuo18,
                  href: workeffyAdminRoutes.WorkeffyDocumentsManage(),
                }
              : null,
          ]),
        ],
      )
    : null;

  const modules = toSection(NAV_SECTION_IDS.Modules, [
    people,
    productivity,
    learning,
    engagement,
    documents,
    hasPermission(FunctionalPermissionKey.ViewAnalyticsV1)
      ? {
          id: NAV_IDS.Analytics,
          label: t('Common:Routes.Analytics'),
          icon: IconChart3OutlineDuo18,
          href: routes.LearnerReports(),
          matchPrefix: true,
        }
      : null,
    hasPermission(FunctionalPermissionKey.UseReport)
      ? toParent(
          {
            id: NAV_IDS.AnalyticsV2,
            label: t('Common:Routes.Analytics'),
            badge: t('Common:New'),
            icon: IconChart3OutlineDuo18,
          },
          [
            toGroup(NAV_GROUP_IDS.AnalyticsV2Main, [
              {
                id: NAV_IDS.AnalyticsV2Overview,
                label: t('Common:Routes.Overview'),
                icon: IconGridOutlineDuo18,
                href: routes.AnalyticsAppOverview(),
              },
              {
                id: NAV_IDS.AnalyticsV2Dashboards,
                label: t('Common:Routes.Dashboards'),
                icon: IconSquareLayoutGrid4OutlineDuo18,
                href: routes.AnalyticsAppDashboards(),
                matchPrefix: true,
              },
              {
                id: NAV_IDS.AnalyticsV2Reports,
                label: t('Common:Reports'),
                icon: IconClipboardContentOutlineDuo18,
                href: routes.AnalyticsAppReports(),
                matchPrefix: true,
              },
              {
                id: NAV_IDS.AnalyticsV2Exports,
                label: t('Common:Routes.Exports'),
                icon: IconUpload3OutlineDuo18,
                href: routes.AnalyticsAppExports(),
                matchPrefix: true,
              },
            ]),
          ],
        )
      : null,
  ]);

  const footer = toSection(NAV_SECTION_IDS.Footer, [
    hasPermission(FunctionalPermissionKey.ManageAcademy)
      ? {
          id: NAV_IDS.Settings,
          label: t('Common:Actions.Settings', 'Settings'),
          icon: IconNut2OutlineDuo18,
          href: routes.Settings(),
          matchPrefix: true,
        }
      : null,
  ]);

  return compact([primary, modules, footer]);
};
